const OFFLINE_PAGE_CHECK_TIMEOUT_MS = 5000;
const OFFLINE_MODAL_ID = "offline-maintenance-modal";

/**
 * Restituisce l'URL della pagina offline/manutenzione ospitata su CDN.
 * Configurabile tramite la env var VITE_OFFLINE_PAGE_URL.
 * Se non configurata, il meccanismo è disattivato (ritorna "").
 *
 * @returns {string} URL assoluto della offline.html, oppure "" se non configurato
 */
export function getOfflinePageUrl() {
  return (
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_OFFLINE_PAGE_URL) || ""
  );
}

/**
 * Scarica il contenuto HTML della pagina di manutenzione dalla CDN.
 *
 * Il file offline.html viene caricato manualmente sulla CDN "al bisogno":
 * la sua presenza (HTTP 200) segnala una manutenzione attiva e ne restituisce
 * il contenuto da renderizzare nella modale bloccante.
 *
 * NB: per poter leggere il body cross-origin la risorsa sulla CDN deve rispondere
 * con gli header CORS (Access-Control-Allow-Origin). Qualsiasi errore
 * (rete/CORS/timeout/404) viene trattato come "non pubblicata" (ritorna null)
 * per non bloccare l'avvio dell'app.
 *
 * @returns {Promise<string|null>} l'HTML della pagina se pubblicata, altrimenti null
 */
export async function fetchOfflinePageContent() {
  const url = getOfflinePageUrl();
  if (!url) return null;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), OFFLINE_PAGE_CHECK_TIMEOUT_MS);

    // cache-busting per evitare risposte cache-ate della CDN
    const checkUrl = `${url}${url.includes("?") ? "&" : "?"}_=${Date.now()}`;
    const response = await fetch(checkUrl, {
      method: "GET",
      cache: "no-store",
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Il file mancante torna 404 (con l'index.html dell'app come error document):
    // lo status distingue quindi "pagina pubblicata" (200) da "assente".
    if (!response.ok) return null;

    return await response.text();
  } catch {
    return null;
  }
}

/**
 * Mostra una modale a tutto schermo, bloccante, che renderizza il contenuto
 * HTML della pagina di manutenzione dentro un <iframe srcdoc>.
 *
 * L'iframe isola completamente il documento (stili/script propri) e l'overlay
 * copre l'intera viewport impedendo ogni interazione con l'app sottostante.
 * Idempotente: se la modale è già presente non fa nulla.
 *
 * @param {string} htmlContent contenuto HTML della offline.html
 * @returns {boolean} true se la modale è (o era già) mostrata
 */
export function showOfflineModal(htmlContent) {
  if (document.getElementById(OFFLINE_MODAL_ID)) return true;

  // blocca lo scroll della pagina sottostante
  document.body.style.overflow = "hidden";

  const overlay = document.createElement("div");
  overlay.id = OFFLINE_MODAL_ID;
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Sistema in manutenzione");
  overlay.style.cssText = [
    "position:fixed",
    "inset:0",
    "width:100vw",
    "height:100vh",
    "margin:0",
    "padding:0",
    "border:0",
    "background:#f5f5f5",
    "z-index:2147483647",
  ].join(";");

  const iframe = document.createElement("iframe");
  iframe.title = "Sistema in manutenzione";
  iframe.srcdoc = htmlContent;
  iframe.style.cssText = "width:100%;height:100%;border:0;display:block;";

  overlay.appendChild(iframe);
  document.body.appendChild(overlay);
  return true;
}
